// Shared helpers for YouTube embeds + ".youtube_timestamp" buttons
// (chat answers in LessonGenerator and the Lexical workboard editor).

// Marks every player we control so timestamp clicks can find / pause them
export const YT_PLAYER_SELECTOR = 'iframe[data-yt-player="true"]';

// Video id from any YouTube URL form: youtu.be/ID, watch?v=ID, /embed/ID, /shorts/ID, /live/ID
export const getYoutubeVideoId = (url) => {
  if (!url) return "";
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.split("/")[1] || "";
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    const m = u.pathname.match(/\/(?:embed|shorts|live|v)\/([^/?#]+)/);
    return m ? m[1] : "";
  } catch {
    const m = String(url).match(/(?:youtu\.be\/|[?&]v=|\/(?:embed|shorts|live|v)\/)([\w-]{6,})/);
    return m ? m[1] : "";
  }
};

// enablejsapi=1 is required for postMessage seekTo / playVideo / pauseVideo
export const withYoutubeJsApi = (src) => {
  if (!src) return src;
  try {
    const u = new URL(src);
    if (!/youtube(?:-nocookie)?\.com$/.test(u.hostname.replace(/^www\./, ""))) return src;
    u.searchParams.set("enablejsapi", "1");
    return u.toString();
  } catch {
    return src;
  }
};

export const postYoutubeCommand = (iframe, func, args = []) => {
  iframe?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args }), "*");
};

// "06:37" -> 397 , "1:02:03" -> 3723 , "397" -> 397
export const ytTimestampToSeconds = (text) => {
  const clean = String(text || "").trim();
  if (/^\d+$/.test(clean)) return parseInt(clean, 10);
  const parts = clean.split(":").map((p) => parseInt(p, 10));
  if (!parts.length || parts.some((p) => Number.isNaN(p))) return 0;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
};

// prefer ?t= / ?start= on the data-url ("397", "397s", "6m37s"), else the visible label
export const ytSecondsFromUrl = (url, label) => {
  try {
    const raw = new URL(url).searchParams.get("t") || new URL(url).searchParams.get("start");
    if (raw) {
      if (/[hms]/i.test(raw)) {
        const m = raw.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?/i) || [];
        return (+m[1] || 0) * 3600 + (+m[2] || 0) * 60 + (+m[3] || 0);
      }
      const n = parseInt(raw, 10);
      if (!Number.isNaN(n)) return n;
    }
  } catch {
    /* fall through to label */
  }
  return ytTimestampToSeconds(label);
};

/**
 * Seek + play the player inside `root` whose video id matches `url`, and pause
 * every other player on the page. Falls back to the only player in `root` when
 * there is just one. Returns the iframe that was played, or null if none matched.
 */
export const playYoutubeAt = (root, url, seconds) => {
  const players = Array.from((root || document).querySelectorAll(YT_PLAYER_SELECTOR));
  const targetId = getYoutubeVideoId(url);
  const iframe =
    players.find((p) => targetId && p.dataset.ytId === targetId) ||
    (players.length === 1 ? players[0] : null);
  if (!iframe) return null;

  document.querySelectorAll(YT_PLAYER_SELECTOR).forEach((p) => {
    if (p !== iframe) postYoutubeCommand(p, "pauseVideo");
  });

  try {
    postYoutubeCommand(iframe, "seekTo", [seconds, true]);
    postYoutubeCommand(iframe, "playVideo");
  } catch (err) {
    // API unavailable → reload the player at the timestamp
    try {
      const u = new URL(iframe.src);
      u.searchParams.set("start", String(seconds));
      u.searchParams.set("autoplay", "1");
      iframe.src = u.toString();
    } catch (reloadErr) {
      console.error("youtube timestamp seek failed:", reloadErr);
    }
  }
  return iframe;
};
